// Category groups and their targets, as a share of this month's income (the carried-over amount
// is not income). Pure, shared by the Worker and the PWA so the Budget tab can update as amounts
// change. A target is a range ("50 to 60%") or a single percent ("10%", flagged only when more
// than 2 points away). Percents are compared as shown (rounded to whole numbers).

/** How far a single-percent target may be off before it is flagged. */
export const ABOUT_TOLERANCE_PCT = 2;

/** The group's planned total as a whole percent of income, or null with no income. */
export function percentOfIncome(plannedCents: number, incomeCents: number): number | null {
  return incomeCents > 0 ? Math.round((plannedCents * 100) / incomeCents) : null;
}

/** A range when both ends are set and differ; otherwise a single "about" percent; null with neither. */
export function targetKind(minPct: number | null, maxPct: number | null): 'range' | 'about' | null {
  if (minPct === null && maxPct === null) return null;
  return minPct !== null && maxPct !== null && minPct !== maxPct ? 'range' : 'about';
}

/**
 * The target in words: "50 to 60%" for a range, "About 10%" for a single percent (one number, or
 * the same number in both fields: never "10 to 10%"), or '' with no target.
 */
export function targetLabel(minPct: number | null, maxPct: number | null): string {
  const kind = targetKind(minPct, maxPct);
  if (!kind) return '';
  return kind === 'range' ? `${Math.min(minPct!, maxPct!)} to ${Math.max(minPct!, maxPct!)}%` : `About ${minPct ?? maxPct}%`;
}

/** The target's numbers in a sentence: "50 to 60%" for a range, "10%" for a single percent. */
function targetNumbers(minPct: number | null, maxPct: number | null): string {
  return targetKind(minPct, maxPct) === 'range' ? `${Math.min(minPct!, maxPct!)} to ${Math.max(minPct!, maxPct!)}%` : `${minPct ?? maxPct}%`;
}

/** Is this percent outside the target? false with no target or no income. */
export function outsideTarget(pct: number | null, minPct: number | null, maxPct: number | null): boolean {
  const kind = targetKind(minPct, maxPct);
  if (pct === null || !kind) return false;
  if (kind === 'about') return Math.abs(pct - (minPct ?? maxPct)!) > ABOUT_TOLERANCE_PCT;
  return pct < Math.min(minPct!, maxPct!) || pct > Math.max(minPct!, maxPct!);
}

/**
 * The group's one line under its name: its percent of income and its target, nothing else.
 * - a range: "9% of income, target 20 to 35%";
 * - a single percent (one number, or the same in both fields): "21% of income, target 10%";
 * - no income: "Target: 20 to 35% of income" ("Target: 10% of income"), nothing without one;
 * - no target: "52% of income".
 * `warning` (amber): outside a range, or more than 2 points from a single percent.
 */
export function groupShare(plannedCents: number, incomeCents: number, minPct: number | null, maxPct: number | null): { text: string | null; warning: boolean } {
  const kind = targetKind(minPct, maxPct);
  const pct = percentOfIncome(plannedCents, incomeCents);
  if (pct === null) return { text: kind ? `Target: ${targetNumbers(minPct, maxPct)} of income` : null, warning: false };
  const share = `${pct}% of income`;
  if (!kind) return { text: share, warning: false };
  return { text: `${share}, target ${targetNumbers(minPct, maxPct)}`, warning: outsideTarget(pct, minPct, maxPct) };
}

/**
 * Why a category cannot be dragged into a group of the other kind (the drop snaps back, and the
 * server refuses it). The Group picker in the category drawer does that move.
 */
export const CROSS_KIND =
  'Money set aside is not spending and is planned as transfers you mark done, so a category is not dragged between the two. To change what it is, pick another group under Group when you edit it.';
